import bilAPI from "./api";
import { downloadFile } from "../utils/download";

// The PDFs and keys of a BTS booklet (TZ 4.1–4.2, admins only): print jobs, documents with their download log,
// and the answer keys. Every key opened and every document downloaded is written to the log by the server.
const BOOKLETS = "/exams/booklets";
const JOBS = "/exams/booklet-jobs";
const DOCUMENTS = "/exams/booklet-documents";

// A job is under way while it is queued or running; succeeded, failed and cancelled are final.
export const LIVE_JOB_STATUSES = ["queued", "running"];
export const isLiveJob = (job) => !!job && LIVE_JOB_STATUSES.includes(job.status);

// The name the server gives a document ("BTS-9104-student.pdf", "BTS-9104-KEY-preview.pdf"): the fallback
// when its Content-Disposition is not readable.
export const documentFileName = (document) =>
  `BTS-${document.code}-${document.kind === "key" ? "KEY" : "student"}${document.preview ? "-preview" : ""}.pdf`;

// POST <uuid>/jobs/: a draft makes previews, an active booklet its final PDFs. → {job, created}: 202 a new
// job, 200 the same job already under way. 400 no_sections. 409 booklet_job_running {job: {uuid, status,
// cancel_requested}}: the job in the way, printing other content (an earlier one) or still stopping.
export const startBookletJob = (uuid) =>
  bilAPI.post(`${BOOKLETS}/${uuid}/jobs/`).then((res) => ({ job: res.data, created: res.status === 202 }));

// GET <uuid>/jobs/: the booklet's latest jobs (up to 10), newest first.
export const getBookletJobs = (uuid, { signal } = {}) =>
  bilAPI.get(`${BOOKLETS}/${uuid}/jobs/`, { signal }).then((res) => res.data);

// GET booklet-jobs/<uuid>/: the job (stage, done/total, error, problems, warnings) with its documents.
export const getBookletJob = (jobUuid, { signal } = {}) =>
  bilAPI.get(`${JOBS}/${jobUuid}/`, { signal }).then((res) => res.data);

// POST booklet-jobs/<uuid>/cancel/: a queued job ends at once, a running one within seconds.
export const cancelBookletJob = (jobUuid) => bilAPI.post(`${JOBS}/${jobUuid}/cancel/`).then((res) => res.data);

// GET <uuid>/documents/: current documents first, then the superseded ones.
export const getBookletDocuments = (uuid, { signal } = {}) =>
  bilAPI.get(`${BOOKLETS}/${uuid}/documents/`, { signal }).then((res) => res.data);

// GET booklet-documents/<uuid>/download/: saves the PDF (the download is logged). Errors come as a Blob,
// parsed by downloadFile.
export const downloadBookletDocument = (document) =>
  downloadFile(`${DOCUMENTS}/${document.uuid}/download/`, { fallbackName: documentFileName(document) });

// GET booklet-documents/<uuid>/downloads/: [{created, ip, user: {uuid, username, full_name} | null}].
export const getDocumentDownloads = (documentUuid, { signal } = {}) =>
  bilAPI.get(`${DOCUMENTS}/${documentUuid}/downloads/`, { signal }).then((res) => res.data);

// GET <uuid>/variants/<n>/key/: {code, number, preview, sections: [{heading, answers: [{n, letters, points}]}]}.
export const getVariantKey = (uuid, number, { signal } = {}) =>
  bilAPI.get(`${BOOKLETS}/${uuid}/variants/${number}/key/`, { signal }).then((res) => res.data);

// GET <uuid>/key_table/: every variant's answers as an .xlsx (Вариант, Предмет, №, Ответ, Балл).
export const downloadKeyTable = (booklet) =>
  downloadFile(`${BOOKLETS}/${booklet.uuid}/key_table/`, {
    fallbackName: `BTS-${booklet.round_number}${String(booklet.grade).padStart(2, "0")}-keys.xlsx`,
  });
